import React, { useState } from 'react';
import { Table, Button, Tag, Select } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { SellerSKU } from '../../../services/supplyChainService';
import { Member } from '../../../services/organizationAPI';

interface SKUMappingTableProps {
    dataSource: SellerSKU[];
    loading: boolean;
    onEdit: (record: SellerSKU) => void;
    members: Member[];
    onUpdateOwner: (id: number, ownerId: number) => void;
    internalSkus: any[];
    onUpdateMap: (id: number, internalSkuId: number) => void;
}

export const SKUMappingTable: React.FC<SKUMappingTableProps> = ({
    dataSource, loading, onEdit, members, onUpdateOwner, internalSkus, onUpdateMap
}) => {
    // Track which row is being edited for internal SKU
    const [editingId, setEditingId] = useState<number | null>(null);

    const columns = [
        { title: '店铺', dataIndex: 'shop_name', key: 'shop_name' },
        { title: '平台SKU', dataIndex: 'sku_code', key: 'sku_code', render: (t: string) => <b>{t}</b> },
        { title: 'ASIN', dataIndex: 'asin', key: 'asin' },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (s: string) => <Tag color={s === 'active' ? 'green' : 'default'}>{s ? s.toUpperCase() : 'UNKNOWN'}</Tag>
        },
        {
            title: '内部SKU (双击编辑)',
            dataIndex: 'internal_sku_code',
            key: 'internal_sku_code',
            width: 250,
            render: (text: string, record: SellerSKU) => {
                const isEditing = editingId === record.id;

                if (isEditing) {
                    return (
                        <Select
                            style={{ width: '100%' }}
                            value={record.internal_sku}
                            onChange={(val) => {
                                onUpdateMap(record.id!, val);
                                setEditingId(null);
                            }}
                            onBlur={() => setEditingId(null)}
                            autoFocus
                            defaultOpen
                            showSearch
                            optionFilterProp="label"
                        >
                            {internalSkus.map(sku => (
                                <Select.Option
                                    key={sku.id}
                                    value={sku.id}
                                    label={`${sku.sku_code || sku.name} - ${sku.name}`}
                                >
                                    {sku.sku_code || sku.name} - {sku.name}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }

                return (
                    <div onDoubleClick={() => setEditingId(record.id!)} style={{ cursor: 'pointer' }}>
                        {text ? (
                            <Tag color="blue">{text}</Tag>
                        ) : (
                            <Tag color="red">未映射</Tag>
                        )}
                    </div>
                );
            }
        },
        {
            title: '负责人',
            dataIndex: 'owner',
            key: 'owner',
            width: 150,
            render: (text: number, record: SellerSKU) => (
                <Select
                    style={{ width: '100%' }}
                    placeholder="选择负责人"
                    value={record.owner}
                    onChange={(val) => onUpdateOwner(record.id!, val)}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                        (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {members.map(m => (
                        <Select.Option key={m.id} value={m.user_id}>{m.username}</Select.Option>
                    ))}
                </Select>
            )
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: SellerSKU) => (
                <Button icon={<LinkOutlined />} onClick={() => onEdit(record)}>映射</Button>
            )
        }
    ];

    return (
        <Table
            dataSource={dataSource}
            columns={columns}
            rowKey="id"
            loading={loading}
        />
    );
};
